import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Checkbox } from "../ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { getAttendanceImportYears, incidentImportPreview, incidentImportCommit } from "../../lib/api";

export default function IncidentImportCard() {
  const [years, setYears] = useState([]);
  const [year, setYear] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [rows, setRows] = useState([]);
  const [roster, setRoster] = useState([]);
  const [loading, setLoading] = useState(false);
  const [committing, setCommitting] = useState(false);

  useEffect(() => {
    getAttendanceImportYears()
      .then((r) => { setYears(r.data || []); if ((r.data || []).length) setYear(r.data[0]); })
      .catch(() => {});
  }, []);

  const nameById = useMemo(() => {
    const m = {};
    roster.forEach((s) => { m[s.id] = s.name; });
    return m;
  }, [roster]);

  const onFile = (e) => {
    setFile(e.target.files?.[0] || null);
    setPreview(null);
    setRows([]);
  };

  const handlePreview = async () => {
    if (!file) { toast.error("Choose an Excel (.xlsx) file"); return; }
    setLoading(true);
    try {
      const res = await incidentImportPreview(file);
      const d = res.data;
      setPreview(d);
      setRoster(d.students || []);
      setRows((d.rows || []).map((r) => ({
        ...r,
        student_id: r.suggested_student_id || "",
        involved_ids: r.involved_ids || [],
        include: Boolean(r.suggested_student_id) && !r.errors?.length && !r.duplicate,
      })));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Preview failed");
    } finally { setLoading(false); }
  };

  const updateRow = (idx, patch) => {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };

  const setStudent = (idx, sid) => {
    const r = rows[idx];
    updateRow(idx, { student_id: sid, include: Boolean(sid) && !r.errors?.length });
  };

  const addInvolved = (idx, sid) => {
    if (!sid) return;
    const r = rows[idx];
    if (sid === r.student_id || (r.involved_ids || []).includes(sid)) return;
    updateRow(idx, { involved_ids: [...(r.involved_ids || []), sid] });
  };

  const removeInvolved = (idx, sid) => {
    updateRow(idx, { involved_ids: (rows[idx].involved_ids || []).filter((x) => x !== sid) });
  };

  const selectedCount = useMemo(
    () => rows.filter((r) => r.include && r.student_id && r.date && !r.errors?.length).length,
    [rows],
  );

  const handleCommit = async () => {
    if (!year) { toast.error("Select a school year"); return; }
    const toSend = rows
      .filter((r) => r.include && r.student_id && r.date && !r.errors?.length)
      .map((r) => ({
        student_id: r.student_id,
        involved_ids: r.involved_ids || [],
        date: r.date,
        time: r.time || "",
        incident_type: r.incident_type || "",
        secondary_type: r.secondary_type || "",
        description: r.description,
        action_taken: r.action_taken || "",
        follow_up_needed: Boolean(r.follow_up_needed),
        serious_flag: r.serious_flag || 0,
        staff_involved: r.staff_involved || "",
        import_key: r.import_key || "",
      }));
    if (!toSend.length) { toast.error("No rows selected to import"); return; }
    setCommitting(true);
    try {
      const res = await incidentImportCommit({
        school_year: year,
        source_name: file?.name || "",
        rows: toSend,
      });
      const d = res.data;
      toast.success(`Imported ${d.inserted} incident(s) into ${d.school_year}. Skipped ${d.skipped_duplicates} duplicate(s).`);
      setPreview(null);
      setRows([]);
      setFile(null);
      const input = document.getElementById("incident-import-file");
      if (input) input.value = "";
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed");
    } finally { setCommitting(false); }
  };

  const s = preview?.summary;

  return (
    <Card data-testid="incident-import-card">
      <CardHeader>
        <CardTitle>Import Historical Incident Reports</CardTitle>
        <CardDescription>
          Upload your incident log spreadsheet (.xlsx). Each row becomes an incident report on the
          student&apos;s file and in the Reports incident summaries. Review and pick the right student for any
          row before importing — you can also attach additional students who were involved.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">School Year</label>
            <Select value={year} onValueChange={setYear}>
              <SelectTrigger className="w-40" data-testid="incident-import-year">
                <SelectValue placeholder="Select year" />
              </SelectTrigger>
              <SelectContent>
                {years.map((y) => (
                  <SelectItem key={y} value={y} data-testid={`incident-import-year-${y}`}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <input
            id="incident-import-file"
            type="file"
            accept=".xlsx"
            onChange={onFile}
            className="text-sm"
            data-testid="incident-import-file"
          />
          <Button onClick={handlePreview} disabled={loading || !file} className="bg-[#00305E] hover:bg-[#00305E]/90" data-testid="incident-import-preview-btn">
            {loading ? "Analyzing…" : "Preview Import"}
          </Button>
        </div>

        <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-900">
          The spreadsheet identifies students by first name. Rows we matched automatically are pre-filled;
          anything unknown is left blank for you to pick. Extra columns (cause, location, response, outcome,
          contacts, staff involved) are folded into each report&apos;s details so nothing is lost.
        </div>

        {s && (
          <div className="flex flex-wrap gap-2" data-testid="incident-import-summary">
            <Badge className="bg-emerald-600" data-testid="incident-count-auto">{s.auto_matched} auto-matched</Badge>
            <Badge variant="outline" className="text-amber-700 border-amber-300" data-testid="incident-count-review">{s.needs_review} need a student</Badge>
            <Badge variant="outline" className="text-blue-700 border-blue-300" data-testid="incident-count-dupes">{s.duplicates} duplicate(s)</Badge>
            <Badge variant="outline" className="text-red-700 border-red-300" data-testid="incident-count-errors">{s.errors} error(s)</Badge>
          </div>
        )}

        {rows.length > 0 && (
          <div className="border rounded-lg max-h-[32rem] overflow-auto" data-testid="incident-import-table">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-gray-50 z-10">
                <tr className="text-left border-b">
                  <th className="p-2 font-medium w-10">Import</th>
                  <th className="p-2 font-medium">Date</th>
                  <th className="p-2 font-medium">Type</th>
                  <th className="p-2 font-medium">Incident (from sheet)</th>
                  <th className="p-2 font-medium">For student</th>
                  <th className="p-2 font-medium">Also involved</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, idx) => {
                  const hasErr = r.errors?.length > 0;
                  return (
                    <tr key={r.row} className={`border-b align-top ${hasErr ? "bg-red-50" : r.duplicate ? "bg-amber-50" : "hover:bg-gray-50"}`} data-testid={`incident-row-${r.row}`}>
                      <td className="p-2">
                        <Checkbox
                          checked={r.include}
                          onCheckedChange={(v) => updateRow(idx, { include: Boolean(v) })}
                          disabled={hasErr || !r.student_id}
                          data-testid={`incident-row-include-${r.row}`}
                        />
                      </td>
                      <td className="p-2 whitespace-nowrap">
                        {r.date || <span className="text-red-600 text-xs">no date</span>}
                        {r.time ? <div className="text-xs text-muted-foreground">{r.time}</div> : null}
                        {r.duplicate ? <div className="text-amber-700 text-xs">{r.dup_reason}</div> : null}
                        {hasErr ? <div className="text-red-600 text-xs">{r.errors.join("; ")}</div> : null}
                      </td>
                      <td className="p-2">
                        {r.incident_type || <span className="text-muted-foreground">—</span>}
                        {r.serious_flag > 0 ? <Badge variant="outline" className="ml-1 text-red-700 border-red-300 text-[10px]">serious</Badge> : null}
                      </td>
                      <td className="p-2 max-w-[22rem]">
                        <div className="text-xs text-muted-foreground mb-0.5">
                          sheet: <strong>{r.raw_student || "—"}</strong>
                        </div>
                        <div className="line-clamp-2 text-xs text-gray-600" title={r.description}>{r.description}</div>
                      </td>
                      <td className="p-2 min-w-[12rem]">
                        <Select value={r.student_id || "none"} onValueChange={(v) => setStudent(idx, v === "none" ? "" : v)}>
                          <SelectTrigger className={`h-8 ${!r.student_id ? "border-amber-400" : ""}`} data-testid={`incident-row-student-${r.row}`}>
                            <SelectValue placeholder="Pick student…" />
                          </SelectTrigger>
                          <SelectContent className="max-h-72">
                            <SelectItem value="none">— none —</SelectItem>
                            {roster.map((stu) => (
                              <SelectItem key={stu.id} value={stu.id}>
                                {stu.name}{stu.status && stu.status !== "current" ? ` (${stu.status})` : ""}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="p-2 min-w-[12rem]">
                        <div className="flex flex-wrap gap-1 mb-1">
                          {(r.involved_ids || []).map((sid) => (
                            <Badge key={sid} variant="outline" className="text-xs" data-testid={`incident-row-involved-${r.row}-${sid}`}>
                              {nameById[sid] || "student"}
                              <button type="button" className="ml-1 text-red-500" onClick={() => removeInvolved(idx, sid)}>×</button>
                            </Badge>
                          ))}
                        </div>
                        {r.unresolved_second ? (
                          <div className="text-[11px] text-amber-700 mb-1">sheet 2nd: {r.unresolved_second} (pick below)</div>
                        ) : null}
                        <Select value="add" onValueChange={(v) => { if (v !== "add") addInvolved(idx, v); }}>
                          <SelectTrigger className="h-8 w-full" data-testid={`incident-row-add-involved-${r.row}`}>
                            <SelectValue placeholder="+ add student" />
                          </SelectTrigger>
                          <SelectContent className="max-h-72">
                            <SelectItem value="add">+ add student</SelectItem>
                            {roster.map((stu) => (
                              <SelectItem key={stu.id} value={stu.id}>{stu.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {rows.length > 0 && (
          <div className="flex items-center gap-3 pt-1">
            <Button
              onClick={handleCommit}
              disabled={committing || !selectedCount}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="incident-import-confirm-btn"
            >
              {committing ? "Importing…" : `Confirm & Import ${selectedCount} Incident(s)`}
            </Button>
            <span className="text-xs text-muted-foreground">Previously imported rows are skipped automatically.</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
